import React from 'react';
import {Box, Container, Stack, Typography, useMediaQuery, useTheme} from '@mui/material';
import Banner from '../components/Banner.js';
import FeedbackCarousel from '../components/FeedbackCarousel.js';
import {imagekitStaticURL} from '../util/util.js';
import {withTitle} from '../title.js';
import withMainContainer from '../components/WithMainContainer.js';
import AlignedText from '../components/AlignedText.js';
import Polaroid from '../components/Polaroid.js';
import WhatsAppButton from '../components/WhatsAppButton.js';
import StyledAccordion from '../components/StyledAccordion.js';
import StackWithArrow from '../components/StackWithArrow.js';

const WHATSAPP_MESSAGE = `היי סימונה, אשמח לקבל פרטים על סדנאות ומעגלים 🌸`;

const Counseling = ()=>{
    let theme = useTheme();
    let sm = useMediaQuery(theme.breakpoints.down('sm'));
    return <Stack direction="column">
      <Banner image={imagekitStaticURL`dancing-women.png`}
        placeholderColor="#242328" backgroundPosition="bottom"
        padding={4}>
        <Typography variant="h4" textAlign="center">
          סדנאות ומעגלים שמחברים בין רוח לחומר
        </Typography>
        <Typography textAlign="center">
          לפעמים כל מה שצריך זה מפגש אחד כדי להצית השראה, להעביר ידע, או לפתוח מרחב שיאפשר הקשבה עמוקה, תובנות ופריצת דרך.<br/><br/>
          אני כאן כדי לעזור לכן ליצור את המפגש הזה - בין אם מדובר בערב נשים, מעגל חברתי או סדנה חווייתית.
        </Typography>
      </Banner>
      <Container maxWidth="lg">
        <Stack direction="column" gap={3} paddingBottom={4} paddingTop={4}>
          <Stack gap={4} justifyContent="center"
            direction={sm ? 'column' : 'row'}
            alignItems={sm ? 'center' : null}>
            <Stack direction="column" gap={3} justifyContent="center">
              <AlignedText variant="h5">
                המפגשים שלי נועדו לתת מקום לקסם
              </AlignedText>
              <AlignedText>
                לפתוח דלת קטנה לאנרגיה הגדולה שפועלת מתחת לפני השטח, ולתת למשתתפות הזדמנות להציץ אל מאחורי הקלעים של הקיום, לקבל תשובות, תחושת ריפוי, התרגשות, סקרנות ושייכות.
              </AlignedText>
              <AlignedText>
                בכל מפגש נוצר משהו מיוחד - בין אם זו מדיטציה מודרכת שפותחת דלתות פנימיות, סדנה להכנת קמעות מכושפים או ערב טארוט שבו כל משתתפת מקבלת תשובות לשאלות שמעסיקות אותה ומסר אישי מדויק.
              </AlignedText>
              <AlignedText>
                זוהי חוויה מגבשת ומיוחדת שבמהלכה אנו משתפות, מקבלות תובנות וצוחקות לא פחות ממה שמתרגשות.
              </AlignedText>
              <AlignedText>
                כל קבוצה מביאה איתה משהו אחר, אבל תמיד קורה חיבור, תמיד עולים רגשות, ותמיד יש מישהי שאומרת בסוף:
              </AlignedText>
              <AlignedText italic bold>
                "לא ידעתי עד כמה הייתי צריכה את זה."
              </AlignedText>
            </Stack>
            <Polaroid width="250px" height="250px" src={imagekitStaticURL`hands-circle.png`}/>
          </Stack>
          <Stack justifyContent="center" alignItems="center">
            <Box component="img" maxWidth="200px"
              src={imagekitStaticURL`moon-divider.png`} sx={{objectFit: 'contain'}}/>
          </Stack>
        </Stack>
      </Container>
      <Banner image={imagekitStaticURL`sage-candles-2.png`}
        placeholderColor="#242328" padding={4}>
        <AlignedText variant="h5">
          מה מיוחד בגישה שלי?
        </AlignedText>
        <Typography>
          אני יודעת לזהות בדיוק מה הקבוצה צריכה - גם כשזה לא נאמר במילים.
        </Typography>
        <Typography>
          בין אם מדובר בערב חווייתי או סדנה מעשית, אני מביאה שילוב של עומק, רגישות, הומור, וניסיון שמאפשר לכל אחת למצוא את עצמה בתוך התהליך.
        </Typography>
        <Typography>
          הגישה שלי תמיד מחוברת לקרקע: לא חייבים להיות "רוחניקים" כדי להרגיש, להבין ולהשתתף.
        </Typography>
        <Typography>
          אני יוצרת מרחב שמאפשר לאנשים להיפתח בלי לחץ, להבין מה באמת מדויק להם, ולצאת עם תחושת הקלה, השראה וכיוון.
        </Typography>
      </Banner>
      <Container maxWidth="md">
        <Stack direction="column" gap={4} alignItems="center"
          paddingBottom={4} paddingTop={4}>
          <AlignedText variant="h5">
            הסדנאות והמעגלים שלי נבנים בהתאמה אישית
          </AlignedText>
          <AlignedText>
            יש לכן קבוצה עם צורך מיוחד? אפשר לבנות יחד סדנה או מפגש שמתאים בדיוק לכן.<br/>
            מוזמנות לפנות אליי ולספר לי על הקבוצה והאירוע - ונרכיב יחד את המפגש שלכן.
          </AlignedText>
          <WhatsAppButton message={WHATSAPP_MESSAGE} text="לקביעת שיחת התאמה ללא עלות" variant="contained"/>
          <Box component="img" height="150px"
            src={imagekitStaticURL`hand-growing-plants.png`} sx={{objectFit: 'contain'}}/>
        </Stack>
      </Container>
      <StackWithArrow width="100%"
        backgroundColor={theme.palette.primary.dark}
        paddingBottom={4}
        arrowIndex={0}>
        <Container maxWidth="lg">
          <Typography color={theme.palette.primary.contrastText} align="center" variant="h3" marginTop={4}>
            מה משתתפות מספרות?
          </Typography>
          <FeedbackCarousel tags="workshop-feedback"/>
        </Container>
      </StackWithArrow>
      <Stack direction="column" width="100%" sx={{backgroundColor: 'primary.light'}}
        paddingTop={10} paddingBottom={4}>
        <Container maxWidth="md">
          <Stack direction="column" gap={3}>
            <Typography textAlign="center" variant="h4">
              שאלות נפוצות
            </Typography>
            <Stack direction="column">
              <StyledAccordion title="למי זה מתאים?">
                לכל קבוצה שמחפשת להכניס רגע מחבר ומעורר השראה, של קסם ומשמעות בדרך מיוחדת ולא שגרתית - קבוצות נשים, ימי הולדת ומסיבת רווקות, ערבי גיבוש לארגונים, מרכזים קהילתיים, פסטיבלים ועוד.
              </StyledAccordion>
              <StyledAccordion title="כמה זמן נמשך כל מפגש?">
                תלוי בסוג המפגש ובקבוצה - בדרך כלל בין שעתיים לשלוש.
              </StyledAccordion>
              <StyledAccordion title="איפה מתקיימים המפגשים?">
                אני מגיעה אליכן אל מקום האירוע.<br/>
                חלק מהמפגשים ניתן לעשות גם בזום.
              </StyledAccordion>
              <StyledAccordion title="האם יש עוד סוגי מפגשים?">
                בהחלט! אם יש לכן רעיון אחר, אני תמיד שמחה להתאים את עצמי עם מגוון רחב של כלים וידע.<br/>
                כתבו לי מה אתן מדמיינות ונראה איך מגשימים את זה יחד.
              </StyledAccordion>
            </Stack>
          </Stack>
        </Container>
      </Stack>
      <Stack direction="column" width="100%" sx={{backgroundColor: 'primary.light'}}
        paddingTop={4} paddingBottom={6}>
        <Container maxWidth="md">
          <Stack direction="column" gap={3} alignItems="center">
            <Typography variant="h4" textAlign="center">
              רוצה לבדוק אם זה מתאים לקבוצה שלך?
            </Typography>
            <Typography textAlign="center">
              שלחי לי הודעה עם כמה מילים על הקבוצה ומה את מחפשת - אחזור אלייך לתיאום שיחה קצרה ונבנה יחד את המפגש שיתאים בדיוק לכן.
            </Typography>
            <WhatsAppButton message={WHATSAPP_MESSAGE} text="לקביעת שיחת התאמה ללא עלות" variant="contained"/>
          </Stack>
        </Container>
      </Stack>
    </Stack>;
};

export default withMainContainer(withTitle(`סימונה צ'רי - ליווי עסקי ואסטרטגיה הוליסטית`, Counseling), {headerMargin: 0});
